import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DateElementsFormatter, DateTimeFormatPipe, PrivaLocalizationService } from '@priva/localization';

import { RecurrencyOption } from './recurrency-option.interface';

dayjs.extend(customParseFormat);

@Injectable()
export class RecurrencyOptionsService {
    constructor(
        private readonly localizationService: PrivaLocalizationService,
        private readonly dateTimePipe: DateTimeFormatPipe,
    ) {}

    /**
     * Based on a selected date, get a predefined list of recurrency options
     * @param date the date to get the recurrency options for
     */
    getRecurrencyOptions(selectedDate: Date): RecurrencyOption[] {
        const results: RecurrencyOption[] = [];

        const dayOfWeek = selectedDate.getDay() + 1; // +1 because sunday = 0
        const dayOfMonth = selectedDate.getDate();
        const month = selectedDate.getMonth() + 1; // +1 because january = 0
        // used for example for: 1st monday, 3rd tuesday
        const occurence = Math.ceil(dayOfMonth / 7);

        // options:
        results.push(
            // daily
            {
                cronExpression: `0 0 0 ? * *`,
                displayValue: 'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.DAILY',
                params: of({}),
            },
            // weekly on {day of week}
            {
                cronExpression: `0 0 0 ? * ${dayOfWeek}`,
                displayValue:
                    'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.WEEKLY',
                params: forkJoin([
                    this.dateTimePipe.transform(selectedDate, DateElementsFormatter.LongDay),
                ]).pipe(
                    map(([dayOfWeekParam]) => {
                        return { dayOfWeek: dayOfWeekParam };
                    }),
                ),
            },
            // monthly on {n}th {dayOfWeek}
            {
                cronExpression: `0 0 0 ? 1/1 ${dayOfWeek}#${occurence} *`,
                displayValue:
                    'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.MONHTLY_NTH_DAYOFWEEK',
                params: forkJoin([
                    this.dateTimePipe.transform(selectedDate, DateElementsFormatter.LongDay),
                    this.localizationService.translate('GLOBAL.OCCURENCE.' + occurence),
                ]).pipe(
                    map(([dayOfWeekParam, occurenceParam]) => {
                        return {
                            dayOfWeek: dayOfWeekParam,
                            occurence: occurenceParam,
                        };
                    }),
                ),
            },
            // monthly on day {dayOfMonth}
            {
                cronExpression: `0 0 0 ${dayOfMonth} * ? *`,
                displayValue:
                    'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.MONHTLY_NTH_DAYOFMONTH',
                params: forkJoin([of(dayOfMonth)]).pipe(
                    map(([dayOfMonthParam]) => {
                        return {
                            dayOfMonth: dayOfMonthParam,
                        };
                    }),
                ),
            },
            // quarterly
            {
                cronExpression: `0 0 0 1 1/3 ? *`,
                displayValue:
                    'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.QUARTERLY',
                params: of({}),
            },
            // yearly on {date}
            {
                cronExpression: `0 0 0 ${dayOfMonth} ${month} ? *`,
                displayValue:
                    'REPORT_DIALOG.PUBLICATION_FREQUENCY.FIELD.PUBLICATION_FREQUENCY.OPTIONS.YEARLY',
                params: forkJoin([of(dayjs(selectedDate).format('D MMMM'))]).pipe(
                    map(([formattedDateParam]) => {
                        return {
                            date: formattedDateParam,
                        };
                    }),
                ),
            },
        );

        return results;
    }
}
